<template>
  <g
    class="influence-matrix__element"
    :style="{
      transform: `translate3d(${xPos}px,${yPos}px, 0)`
    }"
    :class="{ 'is-hovered': flags.hover || flags.saveHover }"
    @pointerenter="onHover"
    @pointerleave="flags.hover = false"
    @click="flags.saveHover = !flags.saveHover"
  >
    <!--<circle cx="0" cy="0" r="2"/>-->
    <circle
      cx="2.5"
      cy="2.5"
      :r="points.length * 3"
      class="influence-matrix__element__bg"
      :class="[points[0].className]"
    />
    <text
      :x="textX"
      y="4"
      :text-anchor="textAnchor"
      class="influence-matrix__element__text"
    >
      <tspan v-for="(point, pointIndex) in points" :key="point.elementUid">
        <template v-if="pointIndex > 0"> | </template>
        {{ elements.labels[point.elementUid] }}
      </tspan>
    </text>
    <g
      v-if="flags.hover || flags.saveHover"
      ref="titleBox"
      :transform="`translate(2.5, -1)`"
    >
      <path
        :d="`M0 0 L2 -2 L-2 -2 Z`"
        class="influence-matrix__element__title__bg"
      />
      <rect
        :x="-pos.textWidth / 2 + pos.textOffsetX"
        :width="pos.textWidth"
        height="6"
        :y="-7.5 + pos.textOffsetY"
        class="influence-matrix__element__title__bg"
      />
      <text
        ref="elementTitleText"
        class="influence-matrix__element__title"
        :x="pos.textOffsetX"
        :y="-3 + pos.textOffsetY"
        text-anchor="middle"
      >
        {{ allElementTitles.join(", ") }}
      </text>
    </g>
  </g>
</template>

<script setup lang="ts">
import { reactive, computed, ref, watch, onMounted, nextTick } from "vue";
import TweenService from "@/helpers/utility/TweenService.js";
import { useElements } from "@editor/items/element/elements";
import type { Uid } from "@/helpers/utility/Id";

const props = defineProps<{
  x: number;
  y: number;
  points: {
    active: number;
    passive: number;
    elementUid: Uid<"target" | "resource" | "index">;
    className?: string;
  }[];
}>();

const emit = defineEmits<{
  (e: "hover"): void;
}>();

const elementTitleText = ref<SVGTextElement>();
const elements = useElements();

const flags = reactive({
  saveHover: false,
  hover: false
});

const pos = reactive({
  textWidth: 10,
  textOffsetX: 0,
  textOffsetY: 0,
  tweenedX: 0,
  tweenedY: 0
});

const xPos = computed(() => {
  return props.x * 95;
});
const yPos = computed(() => {
  return -95 * pos.tweenedY + 95;
});
const allElementTitles = computed(() => {
  return props.points.map((point) => elements.byUid[point.elementUid]?.title ?? "");
});

const textX = computed(() => {
  if (props.x < 0.05) return 1.5;
  if (props.x > 0.95) return 3.75;
  return 2.5;
});
const textAnchor = computed(() => {
  if (props.x < 0.05) return "start";
  if (props.x > 0.95) return "end";
  return "middle";
});

watch(() => props.x, (newValue) => {
  tweenProperty("tweenedX", newValue);
});
watch(() => props.y, (newValue) => {
  tweenProperty("tweenedY", newValue);
});
    
onMounted(() => {
  tweenProperty("tweenedX", props.x);
  tweenProperty("tweenedY", props.y);
});

function onHover() {
  flags.hover = true;
  pos.textOffsetX = 0;
  pos.textOffsetY = 0;
  emit("hover");
  nextTick(() => {
    const text = elementTitleText.value;
    if (text) {
      pos.textWidth = text.getBBox().width + 2;
    }

    // fix X offset
    let textRight = xPos.value + 2.5 + pos.textWidth / 2;
    let textLeft = xPos.value + 2.5 - pos.textWidth / 2;
    if (textRight > 100) {
      pos.textOffsetX = 100 - textRight;
    } else if (textLeft < 0) {
      pos.textOffsetX = 0 - textLeft;
    }

    // fix Y offset
    let textTop = yPos.value - 9;
    if (textTop < 0) {
      pos.textOffsetY = 0 - textTop;
    }
  });
}
const tweenProperty = TweenService.tweenProperty.bind(pos);
</script>

<style lang="scss">
.influence-matrix__element {
  transition: all 500ms;
  &__bg {
    fill: #000;
    transition: fill 500ms;
    &.is-active {
      fill: var(--color-priority-active);
    }
    &.is-critical {
      fill: var(--color-priority-critical);
    }
    &.is-buffering {
      fill: var(--color-priority-buffering);
    }
    &.is-passive {
      fill: var(--color-priority-passive);
    }
    .is-hovered > & {
      stroke: var(--color-action);
      stroke-width: 1px;
    }
  }
  &:active &__bg {
    fill: var(--color-background-deep);
  }
  &__text {
    fill: #000;
    cursor: default;
    user-select: none;
  }
  &_anim {
    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
  }
  &__title {
    fill: var(--color-action-contrast);
    &__bg {
      fill: var(--color-action);
    }
  }
}
</style>
